import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthLayout } from "@/components/PublicLayout";
import { LegalModal } from "@/components/LegalModal";
import { OtpInput } from "@/components/OtpInput";

const DESIGNATIONS = ["Laboratory Director", "Quality Manager", "Technical Manager", "Pathologist", "Laboratory Owner", "Administrator", "Consultant"];

export default function Signup() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [f, setF] = useState({ name: "", email: "", password: "", confirm: "", lab_name: "", designation: "" });
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [modal, setModal] = useState(null);
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) return toast.error("Password must be at least 8 characters");
    if (f.password !== f.confirm) return toast.error("Passwords do not match");
    if (!terms) return setModal("terms");
    if (!privacy) return setModal("privacy");
    setBusy(true);
    try {
      await api.post("/auth/register", { ...f, accepted_terms: terms, accepted_privacy: privacy });
      setStep(2);
      toast.success(`Verification code sent to ${f.email}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register/verify", { email: f.email, otp });
      sessionStorage.setItem("ic_onboard", "1");
      setUser(data);
      toast.success("Welcome to isoCred™! Let's set up your laboratory profile.");
      nav("/profile");
    } catch (err) { toast.error(errMsg(err)); setOtp(""); } finally { setBusy(false); }
  };
  const accept = () => {
    if (modal === "terms") { setTerms(true); setModal(privacy ? null : "privacy"); } else { setPrivacy(true); setModal(null); }
  };
  const LegalRow = ({ kind, ok, label }) => (
    <button type="button" onClick={() => setModal(kind)} className="flex items-center gap-2.5 text-sm w-full text-left" data-testid={`signup-open-${kind}`}>
      <Checkbox checked={ok} className="pointer-events-none" data-testid={`signup-${kind}-checkbox`} />
      <span>I accept the <span className="text-[#48217A] font-medium underline">{label}</span></span>
      {ok && <CheckCircle2 className="h-4 w-4 text-[#2E9B65] ml-auto" />}
    </button>
  );

  return (
    <AuthLayout title="Create your laboratory workspace" subtitle="You will be the primary user and can add team members with roles.">
      {step === 1 ? (
        <form onSubmit={submit} className="space-y-3.5" data-testid="signup-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2"><Label>Laboratory name</Label><Input required value={f.lab_name} onChange={set("lab_name")} data-testid="signup-lab-name-input" /></div>
            <div className="space-y-1.5"><Label>Your name</Label><Input required value={f.name} onChange={set("name")} data-testid="signup-name-input" /></div>
            <div className="space-y-1.5"><Label>Designation</Label>
              <Select value={f.designation} onValueChange={(v) => setF({ ...f, designation: v })}>
                <SelectTrigger data-testid="signup-designation-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{DESIGNATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2"><Label>Work email</Label><Input type="email" required value={f.email} onChange={set("email")} data-testid="signup-email-input" /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input type="password" required value={f.password} onChange={set("password")} data-testid="signup-password-input" /></div>
            <div className="space-y-1.5"><Label>Confirm</Label><Input type="password" required value={f.confirm} onChange={set("confirm")} data-testid="signup-confirm-input" /></div>
          </div>
          <div className="rounded-lg bg-[#F3EFFA] p-3 space-y-2.5">
            <LegalRow kind="terms" ok={terms} label="Terms & Conditions" />
            <LegalRow kind="privacy" ok={privacy} label="Privacy Policy" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-[#48217A] hover:bg-[#3B1A66]" data-testid="signup-submit-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create workspace
          </Button>
          <p className="text-center text-sm text-[#5A6B87]">Already registered? <Link to="/login" className="text-[#48217A] font-medium hover:underline" data-testid="goto-login-link">Sign in</Link></p>
        </form>
      ) : (
        <div className="space-y-5" data-testid="signup-otp-step">
          <p className="text-sm text-[#5A6B87]">Enter the code sent to <b className="text-[#172238]">{f.email}</b> to verify your email.</p>
          <OtpInput value={otp} onChange={setOtp} testid="signup-otp-input" />
          <Button disabled={otp.length !== 6 || busy} onClick={verify} className="w-full bg-[#2E9B65] hover:bg-[#258254]" data-testid="signup-otp-verify-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Verify & create workspace
          </Button>
          <button onClick={() => api.post("/auth/otp/resend", { email: f.email, purpose: "register" }).then(() => toast.success("Code resent")).catch((e) => toast.error(errMsg(e)))}
            className="text-xs text-[#48217A] hover:underline" data-testid="signup-resend-otp-button">Resend code</button>
        </div>
      )}
      <LegalModal open={!!modal} kind={modal || "terms"} onAccept={accept} onClose={() => setModal(null)} />
    </AuthLayout>
  );
}
